import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import BrandMark from "@/components/BrandMark";
import { IDS } from "@/constants/testIds";
import { Send } from "lucide-react";

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/chat/history");
      setMessages(data.messages);
    })();
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { message: msg });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (data.passive_logged) toast("Logged that wear silently ✓");
    } catch (e) {
      toast.error("Stylist unavailable. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain flex flex-col" style={{ paddingBottom: 0 }}>
      <div className="max-w-md mx-auto w-full px-6 pt-12">
        <BrandMark className="mb-4" />
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500">
          Your stylist
        </div>
        <h1 className="font-[Cormorant_Garamond] text-3xl leading-tight tracking-tight text-stone-900">
          Ask me anything.
        </h1>
      </div>

      <div
        ref={listRef}
        className="flex-1 overflow-y-auto max-w-md mx-auto w-full px-6 pt-6 space-y-3"
        style={{ paddingBottom: "calc(9rem + env(safe-area-inset-bottom, 0.75rem))" }}
      >
        {messages.length === 0 && (
          <div className="text-center mt-16">
            <p className="font-[Cormorant_Garamond] text-xl italic text-stone-500 leading-relaxed">
              &ldquo;I&rsquo;m meeting my in-laws for dinner —<br />what do I wear?&rdquo;
            </p>
            <p className="mt-3 text-xs font-[Manrope] text-stone-400 tracking-wide">try anything like this</p>
          </div>
        )}
        {messages.map((m, idx) => (
          <div
            key={m.message_id || `${m.role}-${idx}`}
            data-testid={IDS.chatMessage(idx)}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] px-4 py-3 rounded-2xl font-[Manrope] text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#6B7A54] text-white rounded-tr-none shadow-[0_2px_8px_rgba(107, 122, 84,0.15)]"
                  : "bg-white text-stone-800 border border-stone-200 rounded-tl-none"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="max-w-[85%] px-4 py-3 rounded-2xl bg-white border border-stone-200 rounded-tl-none">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-pulse" />
                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="fixed left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-30"
        style={{ bottom: "calc(4.75rem + env(safe-area-inset-bottom, 0.75rem))" }}
      >
        <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 shadow-[0_16px_40px_rgba(28,25,23,0.08)]">
          <input
            data-testid={IDS.chatInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Message your stylist…"
            className="flex-1 bg-transparent outline-none text-sm font-[Manrope] text-stone-800 placeholder:text-stone-400"
          />
          <button
            data-testid={IDS.chatSend}
            onClick={send}
            disabled={sending || !input.trim()}
            className="h-9 w-9 rounded-full bg-[#6B7A54] text-white flex items-center justify-center disabled:opacity-50"
          >
            <Send size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
